import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    
    // Update ref when navigate changes
    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);

    useEffect(() => {
        // Check if user is logged in on mount
        const userData = localStorage.getItem('user_sis');
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setUser(parsedUser);
                // If we're on the login page, redirect to home
                if (window.location.hash === '#/login') {
                    navigateRef.current('/', { replace: true });
                }
            } catch (error) {
                localStorage.removeItem('user_sis');
                navigateRef.current('/login', { replace: true });
            }
        } else {
            // If no user data and not on login page, redirect to login
            if (window.location.hash !== '#/login') {
                navigateRef.current('/login', { replace: true });
            }
        }
        setLoading(false);
    }, []); // No dependencies needed

    const login = async (credentials) => {
        try {
            const response = await axios.post(
                "https://servermain.sisrevolution.com/api/schools/login/",
                credentials,
                { timeout: 10000 }
            );
            
            if (response.status === 200) {
                const userData = response.data;
                localStorage.setItem('user_sis', JSON.stringify(userData));
                setUser(userData);
                navigate('/', { replace: true });
                return { success: true };
            }
            return {
                success: false,
                error: 'Login failed'
            };
        } catch (error) {
            if (error.code === 'ECONNABORTED' || error.request) {
                return {
                    success: false,
                    error: 'Server is not running. Please start the server on localhost:3008'
                };
            }
            return {
                success: false,
                error: error.response?.data?.message || 'Login failed'
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('user_sis');
        setUser(null);
        navigate('/login', { replace: true });
    };

    const value = {
        user,
        loading,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 